import { IPoint2d } from './IPoint2d';
import { Point2dUtils } from './Point2dUtils';
import { thresholdValue } from './const';
import { normalizeAngle } from './utils';

export class Circle2d {
  private _center: IPoint2d;
  private _radius: number;

  constructor(options: { center: IPoint2d; radius: number }) {
    this._center = options.center;
    this._radius = options.radius;
  }

  get center(): IPoint2d {
    return this._center;
  }

  get radius(): number {
    return this._radius;
  }

  // 是否相切
  isTangentTo(other: Circle2d): boolean {
    const distance = Point2dUtils.distanceTo(this._center, other._center);
    return Math.abs(distance - this._radius - other._radius) < thresholdValue;
  }

  // 正数在圆外，负数在圆内，0在圆上
  pointDistance(point: IPoint2d): number {
    const distance = Point2dUtils.distanceTo(this._center, point) - this._radius;
    if (Math.abs(distance) < thresholdValue) return 0;
    return distance;
  }

  intersect(other: Circle2d): IPoint2d[] | null {
    const dx = other._center.x - this._center.x;
    const dy = other._center.y - this._center.y;
    const d = Math.sqrt(dx * dx + dy * dy);

    // 两圆无交点：外离或内含
    if (d > this._radius + other._radius + thresholdValue) return null;
    if (d < Math.abs(this._radius - other._radius) - thresholdValue) return null;
    // 两圆同心
    if (d < thresholdValue) return null;

    const r1 = this._radius;
    const r2 = other._radius;

    // 交点连线到圆心1的距离
    const a = (r1 * r1 - r2 * r2 + d * d) / (2 * d);
    const h2 = r1 * r1 - a * a;

    // 两圆相切：唯一交点
    if (h2 <= thresholdValue * thresholdValue) {
      return [{ x: this._center.x + (a / d) * dx, y: this._center.y + (a / d) * dy }];
    }

    const h = Math.sqrt(h2);
    // 垂足（交点连线与圆心连线的交点）
    const mx = this._center.x + (a / d) * dx;
    const my = this._center.y + (a / d) * dy;
    // 沿垂直方向偏移 h
    const px = (h / d) * dy;
    const py = (h / d) * dx;

    return [
      { x: mx + px, y: my - py },
      { x: mx - px, y: my + py }
    ];
  }

  getPointAngle(point: IPoint2d): number {
    return normalizeAngle(Math.atan2(point.y - this._center.y, point.x - this._center.x));
  }

  isPointInCircle(point: IPoint2d, excludeOnCircle: boolean = false): boolean {
    return Point2dUtils.distanceTo(this._center, point) < this._radius + thresholdValue * (excludeOnCircle ? -1 : 1);
  }

  getPointOnCircle(angle: number): IPoint2d {
    return {
      x: this._center.x + this._radius * Math.cos(angle),
      y: this._center.y + this._radius * Math.sin(angle)
    };
  }
}
